import { Link } from "react-router-dom";
import RevealOnScroll from "../components/RevealOnScroll";
import { BRAND_ICON } from "../config/brand";
import { ACCOUNTING_PRICING_PACKAGES } from "../constants/accountingPricing";
import { useBrand } from "../context/BrandContext";
import useParallaxScroll from "../hooks/useParallaxScroll";
import "./SalonBusinessCpaLanding.css";

const imageBase = "/Ellyanna Accounting Images";

const focusRingClass =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-500";

const primaryButtonClass = `inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-rose-500 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 sm:w-auto ${focusRingClass}`;
const secondaryButtonClass = `inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-slate-400 hover:bg-slate-50 sm:w-auto ${focusRingClass}`;
const navBookingButtonClass = `inline-flex h-10 shrink-0 items-center justify-center rounded-md border border-sky-300/45 bg-[#3f4e8b] px-4 text-sm font-semibold text-slate-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition hover:border-sky-200/60 hover:bg-[#47599d] ${focusRingClass}`;
const navLoginButtonClass = `inline-flex h-10 items-center justify-center rounded-md border border-[#44529b] bg-[#2f3566] px-4 text-sm font-semibold text-white transition hover:bg-[#252b57] ${focusRingClass}`;
const navRegisterButtonClass = `inline-flex h-10 items-center justify-center rounded-md bg-rose-500 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 ${focusRingClass}`;
const cardClass =
  "min-w-0 rounded-[28px] border border-white/80 bg-white shadow-[0_24px_80px_rgba(47,53,102,0.12)]";
const smallCardClass =
  "rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_14px_45px_rgba(47,53,102,0.08)]";

const serviceCards = [
  {
    title: "Tax time stays easier",
    body:
      "Keep personal tax, business tax, corporate tax, GST/HST, and year-end records in one organized place.",
  },
  {
    title: "Payroll and owner pay",
    body:
      "Organize employee pay, owner wages, tips, commissions, bonuses, dividends, and payroll details.",
  },
  {
    title: "Cleaner bookkeeping",
    body:
      "Turn receipts, bank activity, service sales, product sales, and payment reports into clearer records.",
  },
];

const connectedCards = [
  {
    title: "Salon activity helps your records",
    body:
      "Appointments, services, product sales, clients, and daily records from Ellyanna Booking can help reduce repeat entry when available.",
  },
  {
    title: "Pay details stay easier to explain",
    body:
      "Staff work, tips, commissions, bonuses, owner wages, and dividend notes stay beside the checklists that need them.",
  },
  {
    title: "Checklists match salon work",
    body:
      "Start from real salon activity, then move into bookkeeping, GST/HST, payroll, and tax-readiness steps.",
  },
];

const clientGroups = [
  {
    label: "Employee beauty professionals",
    body: "Organize T4 income, tips, commissions, bonuses, and personal tax records.",
  },
  {
    label: "Independent professionals",
    body: "Track income, expenses, GST/HST, receipts, and owner pay for your beauty business.",
  },
  {
    label: "Salon owners",
    body: "Keep payroll, sales, staff pay, product revenue, tax records, and reports organized.",
  },
];

const comparisonCards = [
  {
    label: "Generic accounting software",
    title: "Usually starts with bank feeds and accounting setup.",
    items: [
      "You still have to explain how your salon makes money.",
      "Tips, commissions, rent, payroll, and owner pay can be hard to sort out.",
      "The workflow is not usually built for beauty businesses.",
    ],
  },
  {
    label: "Ellyanna Accounting",
    title: "Starts with your beauty business.",
    tone: "rose",
    items: [
      "Profiles for employees, independent professionals, and salon owners.",
      "Checklists for GST/HST, payroll, bookkeeping, tax, and reports.",
      "Works alongside Ellyanna Booking activity when available.",
    ],
  },
];

const workflowSteps = [
  {
    title: "Choose your profile",
    body:
      "Tell us how you work: employee, independent professional, or salon owner.",
  },
  {
    title: "Upload your records",
    body:
      "Add receipts, statements, CRA letters, payroll records, tax slips, and notes.",
  },
  {
    title: "Follow your checklist",
    body:
      "See what is ready, what is missing, and what needs review.",
  },
];

const moduleCards = [
  "Personal tax",
  "Corporate tax",
  "GST/HST",
  "Payroll",
  "Bookkeeping",
  "Financial reports",
  "Tips and commissions",
  "Owner pay and dividends",
];

function LogoWordmark() {
  return (
    <Link
      to="/"
      className="inline-flex min-w-0 shrink items-center gap-2 lg:gap-3"
      aria-label="Ellyanna Accounting home"
    >
      <img
        src={BRAND_ICON}
        alt=""
        className="h-8 w-8 flex-shrink-0 lg:h-10 lg:w-10"
      />
      <span className="min-w-0 leading-none">
        <span className="block text-base font-semibold text-white lg:text-xl">
          Ellyanna
        </span>
        <span className="mt-1 hidden text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-300 lg:block lg:text-[10px] lg:tracking-[0.22em]">
          Ellyanna Accounting
        </span>
      </span>
    </Link>
  );
}

function Badge({ children }) {
  return (
    <p className="inline-flex rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
      {children}
    </p>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
      <path
        d="m5 12.5 4.2 4.2L19 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HeroVisual({ parallaxStyle }) {
  return (
    <div className={`${cardClass} salon-cpa-hero-visual relative overflow-hidden p-5 md:p-6`}>
      <div className="salon-cpa-hero-photo-frame relative overflow-hidden rounded-[24px] border border-slate-200 bg-slate-950">
        <div className="salon-cpa-hero-photo aspect-[16/11] min-h-[300px]">
          <img
            src={`${imageBase}/6-SBA image.png`}
            alt="Salon owner reviewing Ellyanna Accounting business performance in a bright nail salon."
            className="h-full w-full object-cover opacity-90"
            loading="eager"
            style={parallaxStyle}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/22 via-slate-950/5 to-rose-500/18" />
        <div className="salon-cpa-dashboard-preview absolute bottom-4 left-4 right-4 rounded-2xl border border-white/25 bg-white/90 p-4 shadow-2xl backdrop-blur">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-500">
            Accounting dashboard
          </p>
          <div className="mt-2 grid gap-3 sm:grid-cols-3">
            {["GST/HST", "Payroll", "Tax ready"].map((item) => (
              <div key={item} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-xs font-semibold text-slate-950">{item}</p>
                <p className="mt-1 h-1.5 rounded-full bg-rose-100">
                  <span className="block h-1.5 w-2/3 rounded-full bg-rose-400" />
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="relative mt-4 grid gap-3 md:grid-cols-3">
        {serviceCards.map((card, index) => (
          <div
            key={card.title}
            className="salon-cpa-float-soft rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_14px_38px_rgba(47,53,102,0.08)]"
            style={{ animationDelay: `${index * 320}ms` }}
          >
            <h3 className="text-sm font-semibold text-slate-950">{card.title}</h3>
            <p className="mt-2 text-xs leading-5 text-slate-600">{card.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ClientGroupCard({ label, body, delay }) {
  return (
    <RevealOnScroll
      as="article"
      delay={delay}
      className="rounded-2xl border border-slate-200 bg-slate-50 p-5"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-50 text-rose-500">
        <CheckIcon />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-slate-950">{label}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
    </RevealOnScroll>
  );
}

function WorkflowCard({ title, body, index }) {
  return (
    <RevealOnScroll
      as="article"
      delay={index * 120}
      className={smallCardClass}
    >
      <p className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-950 text-sm font-semibold text-white">
        {index + 1}
      </p>
      <h3 className="mt-4 text-lg font-semibold text-slate-950">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
    </RevealOnScroll>
  );
}

function ComparisonCard({ label, title, items, tone }) {
  const isRose = tone === "rose";
  return (
    <RevealOnScroll
      as="article"
      className={`rounded-2xl border p-5 ${
        isRose ? "border-rose-200 bg-rose-50/80" : "border-slate-200 bg-slate-50"
      }`}
    >
      <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isRose ? "text-rose-500" : "text-slate-500"}`}>
        {label}
      </p>
      <h3 className="mt-3 text-xl font-semibold text-slate-950">{title}</h3>
      <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
        {items.map((item) => (
          <li key={item} className="flex gap-3">
            <span className={`mt-2 h-2 w-2 shrink-0 rounded-full ${isRose ? "bg-rose-400" : "bg-slate-400"}`} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </RevealOnScroll>
  );
}

function ImageCard({ image, title, body, alt, delay }) {
  return (
    <RevealOnScroll
      as="article"
      delay={delay}
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_14px_45px_rgba(47,53,102,0.08)]"
    >
      <div className="aspect-[16/10] overflow-hidden bg-slate-100">
        <img src={`${imageBase}/${image}`} alt={alt} className="h-full w-full object-cover" loading="lazy" />
      </div>
      <div className="p-5">
        <h3 className="text-lg font-semibold text-slate-950">{title}</h3>
        <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
      </div>
    </RevealOnScroll>
  );
}

function PricingPackageCard({ label, audience, priceLabel, range, delay = 0 }) {
  return (
    <RevealOnScroll
      delay={delay}
      className="flex min-h-[220px] flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_14px_45px_rgba(47,53,102,0.08)]"
    >
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-500">
        {audience}
      </div>
      <h3 className="mt-3 text-base font-semibold leading-6 text-slate-950">
        {label}
      </h3>
      <div className="mt-auto pt-5">
        <div className="text-3xl font-semibold text-slate-950">
          {priceLabel}
          <span className="text-sm font-medium text-slate-500">/month</span>
        </div>
        <p className="mt-2 text-sm leading-6 text-slate-600">{range}</p>
      </div>
    </RevealOnScroll>
  );
}

export default function SalonBusinessCpaLanding() {
  const brand = useBrand();
  const parallax = useParallaxScroll();
  const loginUrl = "/login";
  const bookingUrl = brand?.opsLoginUrl || `${brand?.opsHost ? `https://${brand.opsHost}` : "https://ellyanna.com"}/login`;
  const registerUrl = `${brand?.opsHost ? `https://${brand.opsHost}` : "https://ellyanna.com"}/register`;

  return (
    <main className="salon-cpa-home-surface min-h-screen bg-[linear-gradient(135deg,#fff8f8_0%,#f5f7ff_52%,#ffffff_100%)] text-slate-950">
      <header className="sticky top-0 z-[2600] border-b border-slate-700/70 bg-[#2f3566] text-white shadow-sm">
        <div className="mx-auto max-w-[1400px] overflow-visible px-3 py-3 lg:px-4">
          <div className="flex min-w-0 items-center justify-between gap-1.5 lg:gap-3">
            <LogoWordmark />
            <nav className="hidden min-w-0 flex-1 items-center md:flex" aria-label="Ellyanna Accounting navigation">
              <div className="flex min-w-0 flex-nowrap items-center gap-0.5 pl-3 lg:gap-1.5 lg:pl-6">
                <a href="#services" className="inline-flex h-8 shrink-0 items-center rounded-md px-1.5 text-sm font-medium text-slate-200 transition hover:bg-slate-800 hover:text-white lg:h-9 lg:px-2">
                  Services
                </a>
                <a href="#connected" className="inline-flex h-8 shrink-0 items-center rounded-md px-1.5 text-sm font-medium text-slate-200 transition hover:bg-slate-800 hover:text-white lg:h-9 lg:px-2">
                  Connected
                </a>
                <a href="#who-it-helps" className="inline-flex h-8 shrink-0 items-center rounded-md px-1.5 text-sm font-medium text-slate-200 transition hover:bg-slate-800 hover:text-white lg:h-9 lg:px-2">
                  Who it helps
                </a>
                <a href="#pricing" className="inline-flex h-8 shrink-0 items-center rounded-md px-1.5 text-sm font-medium text-slate-200 transition hover:bg-slate-800 hover:text-white lg:h-9 lg:px-2">
                  Pricing
                </a>
                <a href="#why-different" className="inline-flex h-8 shrink-0 items-center rounded-md px-1.5 text-sm font-medium text-slate-200 transition hover:bg-slate-800 hover:text-white lg:h-9 lg:px-2">
                  Why different
                </a>
              </div>
              <div className="ml-auto flex items-center gap-0.5 lg:gap-1.5">
                <a href={bookingUrl} className={navBookingButtonClass}>
                  Open Ellyanna Booking
                </a>
                <a href={loginUrl} className={navLoginButtonClass}>
                  Login
                </a>
                <a href={registerUrl} className={navRegisterButtonClass}>
                  Register
                </a>
              </div>
            </nav>
            <div className="flex shrink-0 items-center gap-2 md:hidden">
              <a href={loginUrl} className={navLoginButtonClass}>
                Login
              </a>
              <a href={registerUrl} className={navRegisterButtonClass}>
                Register
              </a>
            </div>
          </div>
        </div>
      </header>

      <div className="relative mx-auto max-w-6xl space-y-7 px-5 py-8 md:py-10">
        <section className="grid gap-5 lg:grid-cols-[1.02fr,0.98fr] lg:items-stretch" aria-labelledby="salon-cpa-hero-title">
          <RevealOnScroll className={`${cardClass} p-6 md:p-8`}>
            <Badge>Ellyanna Accounting</Badge>
            <h1
              id="salon-cpa-hero-title"
              className="mt-6 max-w-3xl break-words text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl md:leading-tight"
            >
              Accounting, tax, and bookkeeping help for Canadian beauty businesses.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 md:text-lg">
              Run your beauty business with less paperwork stress. Ellyanna Accounting helps nail technicians, independent professionals, and salon owners stay ready for tax, GST/HST, payroll, owner pay, bookkeeping, and reports.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <a href={loginUrl} className={primaryButtonClass}>
                Log in to Ellyanna Accounting
              </a>
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-500">
              Use your Ellyanna Booking login. Available for active Ellyanna Booking subscribers, with guided records and checklists made for salon work.
            </p>
          </RevealOnScroll>

          <RevealOnScroll delay={120} className="min-w-0">
            <HeroVisual parallaxStyle={parallax.mid} />
          </RevealOnScroll>
        </section>

        <section className="grid gap-4 md:grid-cols-3" aria-label="Ellyanna Accounting service preview cards" id="services">
          <ImageCard
            image="5-SBA image.png"
            title="Messy records, clearer next steps"
            body="Receipts, invoices, tax forms, and notes can pile up quickly. Ellyanna Accounting helps organize what to send and what to review."
            alt="Independent beauty professional surrounded by receipts and salon business paperwork."
          />
          <ImageCard
            image="4-SBA image.png"
            title="Built for salon income"
            body="Services, products, tips, commissions, workspace rental, staff pay, and owner income are handled with beauty businesses in mind."
            alt="Accounting advisor reviewing records with a salon professional in a busy nail salon."
            delay={120}
          />
          <ImageCard
            image="3-SBA image.png"
            title="Canadian tax and payroll focus"
            body="Get organized for GST/HST, payroll remittances, T4 income, corporate tax, personal tax, and dividends."
            alt="Canadian salon business meeting with a tax advisor reviewing reports."
            delay={240}
          />
        </section>

        <section className={`${cardClass} overflow-hidden`} id="connected" aria-labelledby="connected-title">
          <div className="grid gap-0 lg:grid-cols-[0.95fr,1.05fr]">
            <RevealOnScroll className="p-6 md:p-8">
              <Badge>Connected records</Badge>
              <h2 id="connected-title" className="mt-5 break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl">
                Connected to the salon work you already do.
              </h2>
              <p className="mt-4 text-base leading-7 text-slate-600">
                Ellyanna Booking tracks appointments, clients, services, sales, staff activity, and daily salon work. Ellyanna Accounting can use that activity, when available, to help organize bookkeeping, GST/HST, payroll, tax, and financial records.
              </p>
              <p className="mt-4 text-base leading-7 text-slate-600">
                Your accounting and tax documents stay in one place, ready for you and authorized team members who help manage records.
              </p>
              <p className="mt-4 rounded-2xl border border-rose-100 bg-rose-50/70 px-4 py-3 text-sm leading-6 text-slate-600">
                Ellyanna Accounting helps organize records and next steps. Final tax, payroll, bookkeeping, and filing decisions should still be reviewed before submission.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <a href={loginUrl} className={primaryButtonClass}>
                  Open your connected checklist
                </a>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={120} className="bg-slate-50/80 p-6 md:p-8">
              <div className="grid gap-3">
                {["Ellyanna Booking activity", "Ellyanna Accounting checklist", "Review and prepare records"].map((step, index) => (
                  <div key={step} className="flex items-center gap-4 rounded-2xl border border-white bg-white p-4 shadow-[0_14px_45px_rgba(47,53,102,0.08)]">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#2f3566] text-sm font-semibold text-white">
                      {index + 1}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-slate-950">{step}</p>
                      <p className="mt-1 text-xs leading-5 text-slate-500">
                        {index === 0
                          ? "Appointments, services, sales, staff activity, and daily records start in the booking system."
                          : index === 1
                          ? "The accounting portal turns available activity into clear document and checklist prompts."
                          : "You review the organized records before bookkeeping, payroll, GST/HST, or tax work moves forward."}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 grid gap-3 md:grid-cols-3">
                {connectedCards.map((card) => (
                  <article key={card.title} className="rounded-2xl border border-slate-200 bg-white p-4">
                    <h3 className="text-sm font-semibold text-slate-950">{card.title}</h3>
                    <p className="mt-2 text-xs leading-5 text-slate-600">{card.body}</p>
                  </article>
                ))}
              </div>
            </RevealOnScroll>
          </div>
        </section>

        <section className={`${cardClass} px-6 py-7 md:px-8`} id="who-it-helps" aria-labelledby="who-it-helps-title">
          <RevealOnScroll className="max-w-3xl">
            <h2 id="who-it-helps-title" className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl">
              For beauty professionals at every stage.
            </h2>
            <p className="mt-3 text-base leading-7 text-slate-600">
              Starting out, working independently, or running a salon? Ellyanna Accounting helps you know what records matter and what comes next.
            </p>
          </RevealOnScroll>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {clientGroups.map((group, index) => (
              <ClientGroupCard key={group.label} {...group} delay={index * 120} />
            ))}
          </div>
        </section>

        <section className="grid gap-5 lg:grid-cols-[0.95fr,1.05fr] lg:items-center" aria-labelledby="modules-title">
          <RevealOnScroll className={smallCardClass}>
            <Badge>What gets organized</Badge>
            <h2 id="modules-title" className="mt-5 break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl">
              Focus on clients. We help organize the numbers.
            </h2>
            <p className="mt-3 text-base leading-7 text-slate-600">
              You should not have to learn accounting software to keep your business ready. Ellyanna Accounting shows what is needed, why it matters, and what happens next.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {moduleCards.map((module) => (
                <div key={module} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-500">
                    <CheckIcon />
                  </span>
                  <span>{module}</span>
                </div>
              ))}
            </div>
          </RevealOnScroll>
          <RevealOnScroll delay={120} className="overflow-hidden rounded-[28px] border border-white/80 bg-white shadow-[0_24px_80px_rgba(47,53,102,0.12)]">
            <img
              src={`${imageBase}/SBA Group images.png`}
              alt="Ellyanna Accounting scenes showing salon tax, bookkeeping, payroll, and accounting support."
              className="h-full min-h-[360px] w-full object-cover"
              loading="lazy"
            />
          </RevealOnScroll>
        </section>

        <section className={`${cardClass} px-6 py-7 md:px-8`} aria-labelledby="workflow-title">
          <RevealOnScroll className="max-w-3xl">
            <h2 id="workflow-title" className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl">
              A simpler way to stay ready.
            </h2>
            <p className="mt-3 text-base leading-7 text-slate-600">
              Start with your beauty business profile, upload the right records, and follow clear checklists.
            </p>
          </RevealOnScroll>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {workflowSteps.map((step, index) => (
              <WorkflowCard key={step.title} {...step} index={index} />
            ))}
          </div>
        </section>

        <section className={`${cardClass} px-6 py-7 md:px-8`} id="pricing" aria-labelledby="pricing-title">
          <RevealOnScroll className="max-w-3xl">
            <Badge>Pricing</Badge>
            <h2 id="pricing-title" className="mt-5 break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl">
              Low-cost accounting support connected to your salon.
            </h2>
            <p className="mt-3 text-base leading-7 text-slate-600">
              Ellyanna Accounting pricing is based on how you work, whether you are incorporated, and how many staff need payroll and record support. Staff access is included with the salon owner package.
            </p>
          </RevealOnScroll>
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {ACCOUNTING_PRICING_PACKAGES.map((pkg, index) => (
              <PricingPackageCard key={pkg.code} {...pkg} delay={index * 60} />
            ))}
          </div>
          <RevealOnScroll className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm leading-6 text-slate-600">
            11+ staff is custom quote. Catch-up cleanup, full manual bookkeeping,
            complex historical records, CRA review support, and multi-location
            work may be quoted separately.
          </RevealOnScroll>
        </section>

        <section className="px-1 py-3 md:px-2" id="why-different" aria-labelledby="why-different-title">
          <RevealOnScroll className="max-w-3xl">
            <h2 id="why-different-title" className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl">
              Built for beauty businesses, not generic bookkeeping.
            </h2>
            <p className="mt-3 text-base leading-7 text-slate-600">
              Ellyanna Accounting starts with how beauty professionals earn, pay, and grow.
            </p>
          </RevealOnScroll>
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            {comparisonCards.map((card) => (
              <ComparisonCard key={card.label} {...card} />
            ))}
          </div>
        </section>

        <section className="relative overflow-hidden rounded-[28px] border border-white/80 bg-gradient-to-br from-white via-rose-50 to-indigo-50 px-6 py-8 shadow-[0_24px_80px_rgba(47,53,102,0.12)] md:px-8" aria-labelledby="final-cta-title">
          <RevealOnScroll className="grid gap-5 lg:grid-cols-[1fr,auto] lg:items-center">
            <div>
              <h2 id="final-cta-title" className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl">
                Keep your beauty business moving while your records stay organized.
              </h2>
              <p className="mt-3 text-base leading-7 text-slate-600">
                Sign in with Ellyanna to open Ellyanna Accounting, complete your profile, upload records, and start with the checklist that fits your business.
              </p>
            </div>
            <div className="flex flex-col items-start gap-3 lg:items-end">
              <div className="salon-cpa-float-soft-slow rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-semibold text-slate-700 shadow-lg" aria-hidden="true">
                Profile, documents, checklists
              </div>
              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap lg:justify-end">
                <a href={loginUrl} className={primaryButtonClass}>
                  Log in to Ellyanna Accounting
                </a>
                <a href="https://ellyanna.com" className={secondaryButtonClass}>
                  Visit Ellyanna Booking
                </a>
              </div>
            </div>
          </RevealOnScroll>
        </section>
      </div>
    </main>
  );
}
